import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  User, 
  Heart, 
  Users, 
  Building, 
  Shield,
  ArrowRight
} from 'lucide-react';

const COMMUNITY_IMAGE = "https://images.unsplash.com/photo-1555069855-e580a9adbf43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwY29tbXVuaXR5JTIwZ3JvdXAlMjBzdXBwb3J0JTIwY2lyY2xlfGVufDB8fHx8MTc3MzI0OTU3M3ww&ixlib=rb-4.1.0&q=85";

const audiences = [
  {
    icon: User,
    title: "Individuals",
    description: "People experiencing behavioral or mental health challenges who need guidance navigating available support resources and services."
  },
  {
    icon: Heart,
    title: "Transitioning Patients",
    description: "Patients transitioning from hospital care to home who need support understanding and accessing ongoing behavioral health resources."
  },
  {
    icon: Users,
    title: "Families",
    description: "Family members and caregivers supporting loved ones with behavioral health challenges who need guidance and resource information."
  },
  {
    icon: Building,
    title: "Community Members",
    description: "Community members seeking information about behavioral health resources and how to access support when needed."
  },
  {
    icon: Shield,
    title: "Veterans",
    description: "Veterans and service members navigating behavioral health services and seeking connection to appropriate support resources."
  },
  {
    icon: Building,
    title: "Organizations",
    description: "Community organizations seeking navigation support partnerships to better serve their constituents with behavioral health needs."
  },
];

const WhoWeServe = () => {
  return (
    <div className="min-h-screen" data-testid="who-we-serve-page">
      {/* Hero Section */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-testid="page-title">
              Who We Serve
            </h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Behavioral Health Navigation & Workforce Agency serves individuals and families who require guidance navigating behavioral health resources.
            </p>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={COMMUNITY_IMAGE} 
                alt="Diverse community support group" 
                className="rounded-2xl shadow-xl object-cover w-full h-[400px]"
              />
            </div>
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-blue mb-6">
                Supporting Our Community
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                We believe that everyone deserves access to the behavioral health support they need. Our navigation services are designed to meet people where they are and help them find the resources that best fit their unique circumstances.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Whether you're an individual seeking support, a family member helping a loved one, or an organization looking to partner with us, we're here to help guide the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Audiences Grid */}
      <section className="py-20 bg-brand-neutral">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-brand-blue mb-4">
              Those We Support
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our services are designed to support a wide range of individuals and organizations within our community.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {audiences.map((audience, index) => {
              const Icon = audience.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-gray-100 hover:border-brand-teal"
                  data-testid={`audience-card-${index}`}
                >
                  <CardContent className="p-0">
                    <div className="w-14 h-14 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-6">
                      <Icon size={28} className="text-brand-orange" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-brand-blue mb-3">
                      {audience.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {audience.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-teal to-brand-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Need Navigation Support?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            If you or someone you know needs help navigating behavioral health resources, we're here to guide you through the process.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/request-support">
              <Button 
                className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-8 py-6 text-base font-semibold"
                data-testid="cta-request-btn"
              >
                Request Support
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-brand-blue rounded-full px-8 py-6 text-base font-semibold"
                data-testid="cta-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeServe;
